import classNames from 'classnames'
import { Fragment, useMemo, useState } from 'react'
import type { ThreatModelContent } from '../../utils/api-client/api-schemas.ts'
import { pluralize } from '../../utils/text-utils.ts'
import type { AttackPathTone } from './attack-paths-graph.css.ts'
import * as styles from './attack-paths-graph.css.ts'
import {
  ATTACK_PATH_NODE_RADIUS,
  ATTACK_PATH_NODE_SIZE,
  attackPathLinkKey,
  curveBetween,
  gateTracedRoutes,
  layOutAttackPaths,
  traceAttackPath,
  traceFocusGuards,
  type PlacedAttackPathLink,
  type PlacedAttackPathNode,
} from './attack-paths-layout.ts'
import { buildAttackPaths, type AttackPathColumnKey, type AttackPathNode } from './attack-paths.ts'
import { ExposureChip } from './exposure-chip.tsx'
import * as detailStyles from './graph-detail.css.ts'
import { GraphViewport } from './graph-viewport.tsx'

/**
 * The threat model as the path it describes: who can reach the system, through what, which defenses
 * stand in the way, which weaknesses are present, and what is at risk.
 *
 * Hovering a node traces the whole chain through it — forwards and backwards — and dims everything
 * else. Clicking one holds that trace and fills the detail pane from the payload's own prose.
 *
 * **Coverage gaps are marked here** — a count in the header, a badge per weakness, a note in the detail
 * pane. They were Threat coverage's subject until that view was folded into this one, and marking them
 * in both would have made the same fact arrive three times on one screen.
 *
 * Marked in both column modes. In five columns a weakness with no incoming control edge is already
 * visibly undefended — the structural version of the same claim — but the badge appears only where that
 * edge is absent, so the two agree rather than restate each other. In two columns there is no such
 * structural cue, and the badge is the only thing saying it.
 */

/**
 * The columns in reading order, and the noun each contributes to prose describing whatever subset is
 * drawn. Keyed on the column rather than on any single one being present: the header note and the
 * graph's accessible name have to name what is on screen, and keying them on `actor` alone described
 * a two-column view while four columns were showing.
 */
const ATTACK_PATH_COLUMN_KEYS = [
  'actor',
  'entry',
  'control',
  'vuln',
  'asset',
] as const satisfies readonly AttackPathColumnKey[]

const COLUMN_NOUNS: Record<AttackPathColumnKey, string> = {
  actor: 'actors',
  entry: 'entry points',
  control: 'security controls',
  vuln: 'weaknesses',
  asset: 'sensitive assets',
}

/** "security controls and weaknesses", "entry points, security controls and weaknesses". */
const listColumns = (columns: readonly AttackPathColumnKey[]): string => {
  const nouns = ATTACK_PATH_COLUMN_KEYS.filter(key => columns.includes(key)).map(key => COLUMN_NOUNS[key])
  const last = nouns.at(-1)
  return nouns.length > 1 ? `${nouns.slice(0, -1).join(', ')} and ${last}` : (last ?? '')
}

/** Threat coverage's badge geometry, kept when its gap language moved here so the mark is unchanged
 * for anyone who knew it there. */
const GAP_BADGE_WIDTH = 34
const GAP_BADGE_HEIGHT = 15

const TRUST_TONES = ['anonymous', 'authenticated', 'privileged', 'service', 'internal'] as const

const toneFor = (node: AttackPathNode): AttackPathTone => {
  if (node.column === 'actor') {
    return TRUST_TONES.find(level => level === node.tag?.trim().toLowerCase()) ?? 'internal'
  }
  if (node.column === 'vuln') {
    return node.relevance === 'present' ? 'present' : node.relevance === 'likely' ? 'likely' : 'unlikely'
  }
  return node.column === 'entry' ? 'entry' : node.column === 'control' ? 'control' : 'asset'
}

/** Two lines at this box width, then an ellipsis; the full string is always in the detail pane. */
const wrapLabel = (label: string): string[] => {
  const words = label.split(/\s+/)
  const lines: string[] = ['']
  const maxChars = 26
  for (const word of words) {
    const line = lines[lines.length - 1] ?? ''
    if (line.length === 0) {
      lines[lines.length - 1] = word
    } else if (`${line} ${word}`.length <= maxChars) {
      lines[lines.length - 1] = `${line} ${word}`
    } else {
      lines.push(word)
    }
  }
  if (lines.length <= 2) {
    return lines
  }
  const [first, second = ''] = lines
  return [first ?? '', `${second.slice(0, maxChars - 1)}…`]
}

export type AttackPathsGraphProps = {
  content: ThreatModelContent
}

export const AttackPathsGraph: React.FC<AttackPathsGraphProps> = ({ content }) => {
  const [selectedId, setSelectedId] = useState<string>()
  const [hoveredId, setHoveredId] = useState<string>()

  const layout = useMemo(() => layOutAttackPaths(buildAttackPaths({ content })), [content])

  // The drawn column set. A column earns its place by holding nodes *and* connecting to the graph, so
  // this is any subset of the five, not a two-or-five switch: a repository with no sensitive assets
  // recorded drops that column for good, and a document generated before the linking pass drops the
  // outer three until it is regenerated. The legend and the reading-order note follow it so neither
  // describes a column that is not on screen.
  const drawnColumns = useMemo(() => layout.columns.map(column => column.key), [layout])
  const activeColumns = useMemo(() => new Set<AttackPathColumnKey>(drawnColumns), [drawnColumns])
  const tracesFullPath = ATTACK_PATH_COLUMN_KEYS.every(key => activeColumns.has(key))
  // Counted off the drawn nodes rather than the payload, so the number always describes what is on
  // screen — the two-column view drops no vulnerability, but that is a property of the layout, not a
  // guarantee this count should assume.
  const gapCount = useMemo(() => layout.nodes.filter(node => node.isGap).length, [layout])

  const focusedId = hoveredId ?? selectedId
  const traced = useMemo(() => (focusedId ? traceAttackPath(focusedId, layout) : undefined), [focusedId, layout])

  /**
   * With an entry point or a weakness focused, a defended path is drawn *through* the controls that
   * defend it on that route — dashed into the control card and out of it, since the whole route may be
   * stopped there — so the direct entry→weakness curve it replaces drops to the dimmed layer. A control
   * touching the focus but standing on no route keeps a dotted tie: one guarding the focused entry point
   * but naming none of its weaknesses, or one naming the focused weakness but guarding none of the entry
   * points that expose it. Any other focus leaves both ends of a
   * route free, so a control card would read as a hub implying edges the payload never states; those
   * keep the class-level bars. See `traceFocusGuards`.
   */
  const guarding = useMemo(() => (focusedId ? traceFocusGuards(focusedId, layout) : undefined), [focusedId, layout])
  const isDrawnTraced = (link: PlacedAttackPathLink): boolean => {
    const key = attackPathLinkKey(link)
    return !!traced?.linkKeys.has(key) && !guarding?.replacedLinkKeys.has(key)
  }
  /** One dashed run per entry→control pair, however many weaknesses it leads on to. */
  const routesIntoControls = useMemo(
    () =>
      [...new Map(guarding?.routes.map(route => [`${route.entry.id}->${route.control.id}`, route])).entries()].map(
        ([key, route]) => ({ key, d: curveBetween(route.entry, route.control) })
      ),
    [guarding]
  )
  /** One dashed run per control→weakness link, however many entry points arrive at it. */
  const routesOutOfControls = useMemo(
    () => [...new Map(guarding?.routes.map(route => [attackPathLinkKey(route.mitigation), route.mitigation])).values()],
    [guarding]
  )

  /**
   * Which of the traced weaknesses a control actually defends, and so where the traced path is contested.
   *
   * Per route whenever the document states `applies_to`: with routes drawn through control cards
   * (`guarding`) no bar is drawn — the card is the gate; from any other focus a bar marks each traced
   * entry→weakness link a control on *that* route names (`gateTracedRoutes`), and a control naming the
   * weakness but linked to none of the traced routes is a dotted tie. Either way the run past a weakness is
   * dashed only when every traced route into it is gated — one open route and it is reached regardless.
   * Without `applies_to` it falls back to `defendedBy`: some control somewhere names the class. A weakness
   * nobody names stays an open run from actor to asset, the structural version of "coverage gap".
   */
  const contested = useMemo(() => {
    if (!traced) {
      return undefined
    }
    const isTraced = (link: PlacedAttackPathLink) => traced.linkKeys.has(attackPathLinkKey(link))
    const beyond = (defendedVulnIds: Set<string>) =>
      new Set(layout.links.filter(link => isTraced(link) && defendedVulnIds.has(link.from)).map(attackPathLinkKey))

    if (guarding) {
      return {
        blocked: [],
        gatedKeys: new Set<string>(),
        tieKeys: new Set<string>(),
        beyondKeys: beyond(guarding.fullyGatedVulnIds),
      }
    }

    const routeGates = gateTracedRoutes(traced, layout)
    if (routeGates) {
      return {
        blocked: layout.links.filter(link => routeGates.gatedRouteKeys.has(attackPathLinkKey(link))),
        gatedKeys: new Set([...routeGates.gatedRouteKeys, ...routeGates.onRouteMitigationKeys]),
        tieKeys: routeGates.offRouteMitigationKeys,
        beyondKeys: beyond(routeGates.defendedVulnIds),
      }
    }

    // A document without `applies_to`: the class-level rule, as before the linking pass.
    const defendedVulnIds = new Set(
      layout.nodes
        .filter(node => traced.nodeIds.has(node.id) && (node.defendedBy?.length ?? 0) > 0)
        .map(node => node.id)
    )
    const gated = layout.links.filter(link => isTraced(link) && defendedVulnIds.has(link.to))
    const controlIds = new Set(layout.nodes.filter(node => node.column === 'control').map(node => node.id))
    return {
      /** Only an entry→weakness curve gets a bar: one leaving a control card already starts at the gate. */
      blocked: gated.filter(link => !controlIds.has(link.from)),
      gatedKeys: new Set(gated.map(attackPathLinkKey)),
      tieKeys: new Set<string>(),
      beyondKeys: beyond(defendedVulnIds),
    }
  }, [traced, layout, guarding])
  /** A traced curve is an unlinked-control tie, a run a control may stop, or a plainly reachable run. */
  const tracedEdgeStyle = (key: string): string => {
    if (contested?.tieKeys.has(key)) {
      return styles.edgeGuard
    }
    if (contested?.gatedKeys.has(key) || contested?.beyondKeys.has(key)) {
      return styles.edgeContingent
    }
    return styles.edgeTraced
  }
  const hasGuardData = useMemo(() => layout.nodes.some(node => node.guardedBy !== undefined), [layout])
  const labelById = useMemo(() => new Map(layout.nodes.map(node => [node.id, node.label])), [layout])

  const shown = layout.nodes.find(node => node.id === selectedId)

  return (
    <div className={styles.layout}>
      <div className={styles.canvasMain}>
        <div className={styles.canvasPanel}>
          <div className={styles.header}>
            <div className={styles.headerTags}>
              <ExposureChip internetFacing={content.exposure.internet_facing} />
              {/* No "N of M covered": it is the arithmetic complement of the gap count, so the two said
                  the same thing twice and the gap is the one worth acting on. */}
              <span className={styles.chipAlarm}>
                <span className={styles.chipAlarmValue}>{gapCount}</span> coverage {pluralize(gapCount, 'gap', 'gaps')}
              </span>
              {/* Purple is travelled, white is not: the two groups carry that split, and each swatch is drawn
                  with the line's own style so the key cannot drift from what the canvas shows. */}
              <div className={styles.edgeLegend} aria-label="Line key">
                <span className={styles.legendGroupLabel}>Attack paths</span>
                <span className={styles.legendItem}>
                  <LineSwatch lineStyle={styles.edgeTraced} />
                  Reachable, no control in the way
                </span>
                <span className={styles.legendItem}>
                  <LineSwatch lineStyle={styles.edgeContingent} />
                  Passes through a control, reachable if it fails
                </span>
                <span className={styles.legendItem}>
                  <span className={styles.legendGate} aria-hidden />
                  Control stands here
                </span>
                {hasGuardData && (
                  <>
                    <span className={styles.legendSeparator} aria-hidden />
                    <span className={styles.legendGroupLabel}>Not linked to a path</span>
                    <span className={styles.legendItem}>
                      <LineSwatch lineStyle={styles.edgeGuard} />
                      Related control, not linked to this path (may still apply)
                    </span>
                  </>
                )}
              </div>
            </div>
            <AttackPathsLegend activeColumns={activeColumns} />
            <p className={styles.headerNote}>
              {tracesFullPath
                ? `Reads left to right: who can reach the system, through what, which defenses stand in the way, which weaknesses are present, and what is at risk. Hover a node to trace the chain through it. Purple lines are attack paths, dashed where a control may stop them.`
                : `Reads left to right: ${listColumns(drawnColumns)}. Hover a node to trace the chain through it. Purple lines are attack paths, dashed where a control may stop them.`}
              {hasGuardData &&
                " Hovering an entry point or a weakness routes its paths through the controls that guard them. A white dotted line marks a related control the threat model doesn't link to any of these paths. It may still apply, so check its scope."}
            </p>
          </div>

          <GraphViewport
            contentSize={layout.contentSize}
            ariaLabel="Attack paths"
            onBackgroundClick={() => setSelectedId(undefined)}
          >
            {scale => (
              <svg
                // `group`, not the default presentational role: an <svg> with no accessible name makes its
                // whole subtree presentational, which drops every `role="button"` node from the
                // accessibility tree while leaving it focusable — the same fix `threat-model-graph.tsx`
                // documents. The GraphViewport's own label covers the surface, not this node's children.
                role="group"
                aria-label={
                  tracesFullPath
                    ? 'Attack paths: actors and entry points through the security controls that stand in the way, to the weaknesses present and the assets they put at risk'
                    : `Attack paths: ${listColumns(drawnColumns)}, connected left to right`
                }
                width={layout.contentSize.width * scale}
                height={layout.contentSize.height * scale}
                viewBox={`0 0 ${layout.contentSize.width} ${layout.contentSize.height}`}
              >
                <g>
                  {layout.columns.map(column => (
                    <g key={column.key}>
                      {/* No inline fontSize: it would override the class, which is what kept these from
                          matching Threat coverage's headings. */}
                      <text className={styles.columnTitle} x={column.x} y={62}>
                        {column.title}
                      </text>
                    </g>
                  ))}
                </g>

                {/* Three layers, in this order, because a line must never appear to cross a card.
                    Untraced edges sit *under* the nodes, whose opaque backing hides them where they pass
                    behind — an entry-to-weakness curve crosses the control column, and at rest that has
                    to read as passing behind those cards, not through them. The traced path then draws
                    *over* the nodes, because following it across the column is the point of tracing, and
                    the gate has to be visible standing in the route. */}
                <g>
                  {layout.links
                    .filter(link => !isDrawnTraced(link))
                    .map(link => (
                      <path
                        key={attackPathLinkKey(link)}
                        d={link.d}
                        className={classNames(styles.edge, traced && styles.edgeDimmed)}
                      />
                    ))}
                </g>

                <g>
                  {layout.nodes.map(node => (
                    <NodeBox
                      key={node.id}
                      node={node}
                      isSelected={node.id === selectedId}
                      isGuarding={!!guarding?.controls.includes(node)}
                      isDimmed={!!traced && !traced.nodeIds.has(node.id) && !guarding?.controls.includes(node)}
                      onSelect={() => setSelectedId(current => (current === node.id ? undefined : node.id))}
                      onHoverChange={isHovered => setHoveredId(isHovered ? node.id : undefined)}
                    />
                  ))}
                </g>

                <g className={styles.tracedLayer}>
                  {guarding?.ties.map(({ from, to }) => (
                    <path
                      key={`guard-${from.id}-${to.id}`}
                      data-testid="attack-path-guard"
                      d={curveBetween(from, to)}
                      className={classNames(styles.edge, styles.edgeGuard)}
                    />
                  ))}
                  {routesIntoControls.map(route => (
                    <path
                      key={`route-in-${route.key}`}
                      data-testid="attack-path-route-into-control"
                      d={route.d}
                      className={classNames(styles.edge, styles.edgeContingent)}
                    />
                  ))}
                  {routesOutOfControls.map(link => (
                    <path
                      key={`route-out-${attackPathLinkKey(link)}`}
                      data-testid="attack-path-route-out-of-control"
                      d={link.d}
                      className={classNames(styles.edge, styles.edgeContingent)}
                    />
                  ))}
                  {layout.links.filter(isDrawnTraced).map(link => {
                    const key = attackPathLinkKey(link)
                    return <path key={key} d={link.d} className={classNames(styles.edge, tracedEdgeStyle(key))} />
                  })}
                  {contested?.blocked.map(link => (
                    <rect
                      key={`block-${attackPathLinkKey(link)}`}
                      data-testid="attack-path-block"
                      className={styles.pathBlock}
                      x={link.gate.at.x - 2.5}
                      y={link.gate.at.y - 9}
                      width={5}
                      height={18}
                      rx={2}
                    />
                  ))}
                </g>
              </svg>
            )}
          </GraphViewport>
        </div>
      </div>

      <aside className={styles.detailPane} aria-label="Attack path detail">
        {shown ? (
          <NodeDetail node={shown} labelById={labelById} />
        ) : (
          <p className={detailStyles.detailEmpty}>Select a node to inspect it.</p>
        )}
      </aside>
    </div>
  )
}

/** A legend line drawn with the canvas's own edge style, so colour, weight and dash match exactly. */
const LineSwatch: React.FC<{ lineStyle: string }> = ({ lineStyle }) => (
  <svg className={styles.legendLine} width={24} height={8} aria-hidden>
    <line x1={1} y1={4} x2={23} y2={4} className={classNames(styles.edge, lineStyle)} />
  </svg>
)

/** Actors carry the trust ramp, vulnerabilities the relevance ramp; the rest is one tone per column. */
const TRUST_LEGEND = [
  { tone: 'anonymous', label: 'Anonymous' },
  { tone: 'authenticated', label: 'Authenticated' },
  { tone: 'privileged', label: 'Privileged' },
  { tone: 'service', label: 'Service' },
  { tone: 'internal', label: 'Internal' },
] as const

/** Between the actor ramp and the relevance ramp, in column order. */
const MIDDLE_LEGEND = [
  { tone: 'entry', label: 'Entry point' },
  { tone: 'control', label: 'Control' },
] as const

const RELEVANCE_LEGEND = [
  { tone: 'present', label: 'Present' },
  { tone: 'likely', label: 'Likely' },
  { tone: 'unlikely', label: 'Unlikely' },
] as const

/** Assets close the row because they close the graph — the key reads in column order. */
const ASSET_LEGEND = [{ tone: 'asset', label: 'Asset' }] as const

type LegendEntry = { tone: AttackPathTone; label: string }

/**
 * The key lists only the swatches for columns that are on screen: the trust ramp and the asset
 * swatch drop out of the two-column view, so nothing names a colour the reader cannot see. Each
 * surviving group is separated from the next, and the groups themselves read in column order.
 */
const AttackPathsLegend: React.FC<{ activeColumns: Set<AttackPathColumnKey> }> = ({ activeColumns }) => {
  const groups: LegendEntry[][] = [
    activeColumns.has('actor') ? [...TRUST_LEGEND] : [],
    MIDDLE_LEGEND.filter(entry => activeColumns.has(entry.tone)),
    activeColumns.has('vuln') ? [...RELEVANCE_LEGEND] : [],
    activeColumns.has('asset') ? [...ASSET_LEGEND] : [],
  ].filter(group => group.length > 0)

  // The gap swatch is not a node tone — it marks a badge, not a fill — so it rides its own trailing
  // group rather than joining the relevance ramp it would otherwise be read as part of.
  const hasGapKey = activeColumns.has('vuln')

  return (
    <div className={styles.legend} aria-label="Colour key">
      {groups.map((group, index) => (
        <Fragment key={group[0]?.tone ?? index}>
          {index > 0 && <span className={styles.legendSeparator} aria-hidden />}
          {group.map(entry => (
            <span key={entry.tone} className={styles.legendItem}>
              <span className={classNames(styles.legendSwatch, styles.legendSwatchColor[entry.tone])} aria-hidden />
              {entry.label}
            </span>
          ))}
        </Fragment>
      ))}
      {hasGapKey && (
        <>
          <span className={styles.legendSeparator} aria-hidden />
          <span className={styles.legendItem}>
            <span className={classNames(styles.legendSwatch, styles.legendSwatchGap)} aria-hidden />
            Coverage gap
          </span>
        </>
      )}
    </div>
  )
}

type NodeBoxProps = {
  node: PlacedAttackPathNode
  isSelected: boolean
  isGuarding: boolean
  isDimmed: boolean
  onSelect: () => unknown
  onHoverChange: (isHovered: boolean) => unknown
}

const NodeBox: React.FC<NodeBoxProps> = ({ node, isSelected, isGuarding, isDimmed, onSelect, onHoverChange }) => {
  const tone = toneFor(node)
  const lines = wrapLabel(node.label)

  return (
    <g
      className={classNames(isDimmed && styles.nodeDimmed)}
      onPointerEnter={() => onHoverChange(true)}
      onPointerLeave={() => onHoverChange(false)}
    >
      {/* An opaque plate under the tinted fill. The fill is a 16% wash so five saturated columns do not
          fight, which also means any line behind a card would show straight through it. */}
      <rect
        className={styles.nodeBacking}
        x={node.x}
        y={node.y}
        width={ATTACK_PATH_NODE_SIZE.width}
        height={ATTACK_PATH_NODE_SIZE.height}
        rx={ATTACK_PATH_NODE_RADIUS}
      />
      <rect
        className={classNames(
          styles.nodeBox,
          styles.nodeFill[tone],
          styles.nodeStroke[tone],
          isGuarding && styles.nodeGuarding,
          isSelected && styles.nodeSelected
        )}
        x={node.x}
        y={node.y}
        width={ATTACK_PATH_NODE_SIZE.width}
        height={ATTACK_PATH_NODE_SIZE.height}
        rx={ATTACK_PATH_NODE_RADIUS}
        tabIndex={0}
        role="button"
        aria-label={node.label}
        aria-pressed={isSelected}
        onClick={onSelect}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onSelect()
          }
        }}
      />
      {lines.map((line, index) => (
        <text key={line} className={styles.nodeLabel} x={node.x + 10} y={node.y + 18 + index * 12} fontSize={10.5}>
          {line}
        </text>
      ))}
      {node.tag && lines.length < 2 && (
        <text className={styles.nodeTag} x={node.x + 10} y={node.y + 38} fontSize={9}>
          {node.tag}
        </text>
      )}
      {/* Straddling the top-right corner rather than sitting on the label's row: it marks the card
          rather than annotating its text, and off the row the label need not shrink for it. */}
      {node.isGap && (
        <>
          <rect
            className={styles.gapBadge}
            x={node.x + ATTACK_PATH_NODE_SIZE.width - GAP_BADGE_WIDTH - 6}
            y={node.y - GAP_BADGE_HEIGHT / 2}
            width={GAP_BADGE_WIDTH}
            height={GAP_BADGE_HEIGHT}
            rx={GAP_BADGE_HEIGHT / 2}
          />
          <text
            className={styles.gapBadgeText}
            x={node.x + ATTACK_PATH_NODE_SIZE.width - GAP_BADGE_WIDTH / 2 - 6}
            y={node.y + 4}
            textAnchor="middle"
          >
            GAP
          </text>
        </>
      )}
    </g>
  )
}

const NodeDetail: React.FC<{ node: AttackPathNode; labelById: Map<string, string> }> = ({ node, labelById }) => (
  <>
    <p className={detailStyles.eyebrow}>{COLUMN_EYEBROWS[node.column]}</p>
    <h4 className={detailStyles.detailTitle}>{node.label}</h4>
    {node.tag && <span className={detailStyles.tag}>{node.tag}</span>}
    {node.fields.map(field => (
      <div key={field.key} className={detailStyles.field}>
        <p className={detailStyles.fieldKey}>{field.key}</p>
        <p className={detailStyles.fieldValue}>{field.value}</p>
      </div>
    ))}
    {node.location && (
      <div className={detailStyles.field}>
        <p className={detailStyles.fieldKey}>Location</p>
        <p className={detailStyles.fieldLocation}>{node.location}</p>
      </div>
    )}
    {node.guardedBy && (
      <RelatedNodes
        title="Guarded by"
        ids={node.guardedBy}
        labelById={labelById}
        emptyText="No control in this threat model guards this entry point."
      />
    )}
    {node.column === 'control' && node.guards && node.guards.length > 0 && (
      <RelatedNodes title="Guards" ids={node.guards} labelById={labelById} />
    )}
    {node.isGap && (
      <div className={styles.gapNote}>
        <span aria-hidden>⚠</span>
        <span>
          <span className={styles.gapNoteStrong}>Coverage gap. </span>
          No control&rsquo;s mitigates list names this weakness.
        </span>
      </div>
    )}
  </>
)

type RelatedNodesProps = {
  title: string
  ids: string[]
  labelById: Map<string, string>
  emptyText?: string
}

const RelatedNodes: React.FC<RelatedNodesProps> = ({ title, ids, labelById, emptyText }) => (
  <div className={detailStyles.field}>
    <p className={detailStyles.fieldKey}>{title}</p>
    {ids.length > 0 ? (
      <ul className={styles.relatedList} aria-label={title}>
        {ids.map(id => (
          <li key={id} className={detailStyles.tag}>
            {labelById.get(id) ?? id}
          </li>
        ))}
      </ul>
    ) : (
      <p className={detailStyles.fieldValue}>{emptyText}</p>
    )}
  </div>
)

/** The singular of each column heading, so the pane names the same thing the column does. */
const COLUMN_EYEBROWS = {
  actor: 'Actor',
  entry: 'Entry point',
  control: 'Security control',
  vuln: 'Vulnerability',
  asset: 'Sensitive asset',
} as const
